import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, Clock, Flag, Scale } from "lucide-react";
import Footer from "@/components/Footer";
import { trackPageView } from "@/lib/analytics";

export default function About() {
  useEffect(() => { trackPageView("about"); }, []);
  const timeline = [
    {
      year: "1937",
      title: "Constitutional Foundation",
      description:
        "Ireland's neutrality is rooted in the 1937 Constitution, reflecting the nation's commitment to peaceful international relations.",
    },
    {
      year: "1958",
      title: "First UN Mission",
      description:
        "Irish troops deploy to Lebanon with UNIFIL, beginning our distinguished peacekeeping tradition.",
    },
    {
      year: "1960",
      title: "Congo Mission",
      description:
        "Irish peacekeepers serve in the Congo, demonstrating courage and sacrifice in the face of danger.",
    },
    {
      year: "1973",
      title: "EU Membership",
      description:
        "Ireland joins the European Economic Community while maintaining Irish neutrality—proof that EU membership and neutrality can coexist.",
    },
    {
      year: "2002",
      title: "Triple Lock",
      description:
        "The 'Triple Lock' mechanism is formalized, requiring UN authorization, Government approval, and Dáil approval for overseas deployments.",
    },
    {
      year: "Present",
      title: "Ongoing Commitment",
      description:
        "Irish peacekeepers continue to serve in missions worldwide, upholding our neutral tradition.",
    },
  ];

  return (
    <main data-testid="about-page" className="pt-24 bg-slate-950">
      {/* Hero Section */}
      <section className="py-16 md:py-24" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
              Understanding Our Position
            </span>
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-slate-50 mt-4 mb-6">
              The History of
              <br />
              <span className="text-emerald-400">Irish Neutrality</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
              Ireland's policy of neutrality is not a recent invention—it
              is a cornerstone of our national identity, shaped by our history,
              values, and commitment to international peace.
            </p>
          </div>
        </div>
      </section>

      {/* What is Neutrality Section */}
      <section className="py-16 bg-slate-900" data-testid="what-is-neutrality">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-emerald-600/20 border border-emerald-500/30">
                  <BookOpen className="w-6 h-6 text-emerald-400" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-slate-100">
                  What is Neutrality?
                </h2>
              </div>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  Neutrality means that Ireland does not participate in
                  military alliances, does not allow foreign military bases on our
                  soil, and does not engage in aggressive military actions against
                  other nations.
                </p>
                <p>
                  It does <strong className="text-slate-100">not</strong> mean
                  passivity or isolation. Ireland actively participates in
                  international affairs through:
                </p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>United Nations peacekeeping missions</li>
                  <li>Humanitarian aid and development assistance</li>
                  <li>Diplomatic mediation and conflict resolution</li>
                  <li>EU civilian crisis management operations</li>
                </ul>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
              <h3 className="font-serif text-xl font-medium text-emerald-400 mb-4">
                The Triple Lock Mechanism
              </h3>
              <p className="text-slate-300 mb-6">
                Irish Defence Forces can only be deployed overseas when all three
                conditions are met:
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-400 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-100">UN Authorization</p>
                    <p className="text-sm text-slate-400">
                      Mission must be mandated or authorized by the United Nations
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-400 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-100">Government Approval</p>
                    <p className="text-sm text-slate-400">
                      The Government must formally approve the deployment
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-400 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-100">Dáil Approval</p>
                    <p className="text-sm text-slate-400">
                      Dáil Éireann must pass a resolution approving the deployment
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 md:py-24 bg-slate-950" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Clock className="w-6 h-6 text-orange-500" />
              <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
                Our Journey
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-100">
              Timeline of Irish Neutrality
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-slate-800" />

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div
                  key={item.year}
                  className={`relative flex items-start gap-8 ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                  data-testid={`timeline-item-${item.year}`}
                >
                  {/* Dot */}
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-emerald-500 -translate-x-1.5 mt-2 z-10" />

                  {/* Content */}
                  <div
                    className={`ml-12 md:ml-0 md:w-5/12 ${
                      index % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16"
                    }`}
                  >
                    <span className="text-emerald-400 font-bold text-lg">
                      {item.year}
                    </span>
                    <h3 className="font-serif text-xl font-medium text-slate-100 mt-1 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-400">{item.description}</p>
                  </div>

                  {/* Spacer for alternating layout */}
                  <div className="hidden md:block md:w-5/12" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Current Threats Section */}
      <section className="py-16 bg-slate-900" data-testid="threats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-orange-600/20 border border-orange-500/30">
                  <Flag className="w-6 h-6 text-orange-400" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-slate-100">
                  Why Is Neutrality Under Threat?
                </h2>
              </div>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  Recent years have seen increasing pressure on Ireland to abandon
                  or dilute our traditional neutrality. These pressures come from
                  various sources:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <span>
                      Proposals to join NATO or establish closer military ties with
                      the alliance
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <span>
                      EU defence integration initiatives like PESCO that may
                      compromise neutrality
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <span>
                      Calls to weaken or abolish the Triple Lock mechanism and the recent vote to pass the Defence (Amendment) Bill 2026: Second Stage
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-orange-500 mt-1">•</span>
                    <span>
                      Increased use of Shannon Airport by US military aircraft
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-emerald-600/20 border border-emerald-500/30">
                  <Scale className="w-6 h-6 text-emerald-400" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-semibold text-slate-100">
                  What Can You Do?
                </h2>
              </div>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  As an Irish citizen, you have the power to make your voice heard.
                  Your elected representatives need to know that the people of
                  Ireland support neutrality.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-500 mt-1">✓</span>
                    <span>
                      Contact your TD, Senator, and local councillors
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-500 mt-1">✓</span>
                    <span>
                      Use our ready-made email template to make it easy
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-500 mt-1">✓</span>
                    <span>
                      Share this campaign with friends and family
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-500 mt-1">✓</span>
                    <span>
                      Support organizations defending Irish neutrality
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/10 via-transparent to-orange-900/10" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-100 mb-6">
            Ready to Take Action?
          </h2>
          <p className="text-lg text-slate-400 mb-10">
            We've made it easy to contact your representatives with a ready-to-send
            email template.
          </p>
          <Link to="/take-action">
            <Button
              data-testid="about-cta-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-6 rounded-full text-lg font-bold transition-all hover:-translate-y-1 shadow-[0_0_30px_-5px_rgba(22,163,74,0.5)]"
            >
              Email the TD in your constituency who voted to pass the Defence (Amendment) Bill 2026: Second Stage
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
